import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Activity, LineChart as LineChartIcon, CheckSquare } from 'lucide-react';
import {
  getCareerOverview, getUserTrackVehicles,
  getChannelStatChannels, getChannelStats, searchUsers,
} from '../../services/api';
import { convertValue, getDisplayUnit } from '../../utils/unitConvert';
import RangeSlider from '../common/RangeSlider';
import useDateRangePresets, { DAY_MS, DATE_PRESETS } from '../common/useDateRangePresets';
import styles from './ChannelStatsPage.module.css';

const LAP_BASES = [
  { key: 'all',   label: 'All laps' },
  { key: 'best',  label: 'Best lap' },
  { key: 'best3', label: 'Best 3' },
  { key: 'best5', label: 'Best 5' },
];

const CATEGORY_ORDER = ['Dynamics', 'Engine', 'Driver inputs', 'Other'];

// Hover explanations for the table columns (native title tooltips).
const TIP = {
  session:  'Track day for this session — click to open it in the analysis view.',
  laps:     'Number of laps included for this session at the current lap basis (All / Best / Best 3 / Best 5).',
  min:      'Lowest value the channel reached across the included laps.',
  max:      'Highest value the channel reached across the included laps.',
  mean:     'Average value, weighted by sample count across the included laps.',
  std:      'Standard deviation (σ) — how much the value varies; lower = steadier.',
  p95:      '95th percentile — the value is at or below this 95% of the time (robust to brief spikes).',
  timeAbove:'Seconds spent with the channel at or ABOVE the threshold, summed over the included laps.',
  timeBelow:'Seconds spent with the channel at or BELOW the threshold, summed over the included laps.',
  distAbove:'Distance travelled while the channel was at or ABOVE the threshold.',
  distBelow:'Distance travelled while the channel was at or BELOW the threshold.',
  ovChannel:'Telemetry channel — click a row to focus it in the chart and detail table above.',
  ovSessions:'How many of the selected sessions logged this channel.',
  ovMin:    'Lowest value across all shown sessions.',
  ovMax:    'Highest value across all shown sessions.',
  ovAvg:    'Average of each session’s mean value.',
  ovUnit:   'Unit the channel is logged in (as reported by the device).',
};

function fmtNum(v, unit) {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return `${v.toFixed(digits)}${unit ? ` ${unit}` : ''}`;
}

function fmtSecs(s) {
  if (s == null) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${(s - m * 60).toFixed(0)}s`;
}

function fmtDist(m, prefs) {
  if (m == null) return '—';
  if (prefs?.speed === 'mph') {
    if (m >= 1609.34) return `${(m / 1609.34).toFixed(2)} mi`;
    return `${(m * 3.28084).toFixed(0)} ft`;
  }
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${m.toFixed(0)} m`;
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' });
}

// Time / distance with the channel value at/above (or below) a threshold, from
// the per-session dual histogram. Mirrors the backend bin layout exactly.
function inBand(block, thr, above) {
  if (!block?.time_hist) return { t: null, d: null };
  const { time_hist, dist_hist, bin_lo, bin_hi, n_bins } = block;
  const w = (bin_hi - bin_lo) / n_bins;
  let t = 0, d = 0;
  for (let i = 0; i < n_bins; i++) {
    const center = bin_lo + (i + 0.5) * w;
    if (above ? center >= thr : center <= thr) { t += time_hist[i]; d += dist_hist[i] || 0; }
  }
  return { t, d };
}

// Convert a channel block (values in the device's logged unit) into the user's
// preferred units. Point stats and histogram edges go through convertValue;
// σ is a SCALE not a point, so it takes only the conversion's slope (e.g. °C→°F
// scales by 1.8, no +32 offset). Non-convertible units pass through unchanged.
function convChannel(block, prefs) {
  if (!block) return block;
  const unit = block.unit || '';
  const dispUnit = getDisplayUnit(unit, prefs || {});
  const v = (x) => (x == null ? x : convertValue(x, unit, prefs || {}));
  const slope = convertValue(1, unit, prefs || {}) - convertValue(0, unit, prefs || {});
  const sd = (x) => (x == null ? x : x * slope);
  const sessions = (block.sessions || []).map(s => ({
    ...s,
    min: v(s.min), max: v(s.max), mean: v(s.mean),
    median: v(s.median), p05: v(s.p05), p95: v(s.p95),
    std: sd(s.std),
    bin_lo: v(s.bin_lo), bin_hi: v(s.bin_hi),
  }));
  return { ...block, unit: dispUnit, sessions };
}

// Stable header sort: nulls last, strings localeCompare, numbers numeric.
function sortRows(rows, sort, accessors) {
  if (!sort?.key || !accessors[sort.key]) return rows;
  const get = accessors[sort.key];
  const arr = [...rows].sort((a, b) => {
    const va = get(a), vb = get(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return typeof va === 'string' ? va.localeCompare(vb) : va - vb;
  });
  return sort.dir === 'desc' ? arr.reverse() : arr;
}

export default function ChannelStatsPage({
  onBack, onLoadSession, initialParams, onParamsChange, currentUser, unitPrefs,
  onGoToLeaderboard, onGoToProgress, onGoToSearch, onGoToSimLab, onOpenUserSettings, onLogout, driverProfile,
}) {
  const [tracks, setTracks] = useState([]);
  const [trackId, setTrackId] = useState(initialParams?.trackId || null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(initialParams?.vehicleId ?? null);

  const [lapBasis, setLapBasis] = useState(initialParams?.lapBasis || 'all');
  const [channelMeta, setChannelMeta] = useState([]);   // [{key,label,category,unit,coverage,n_sessions,curated}]
  const [totalSessions, setTotalSessions] = useState(0);
  const [selected, setSelected] = useState(new Set());  // canonical keys in the overview
  const [showAll, setShowAll] = useState(initialParams?.showAll ?? false);
  const [chanFilter, setChanFilter] = useState(initialParams?.chanFilter || '');     // channel picker text filter
  const [focusKey, setFocusKey] = useState(null);
  // A restored channel selection only has keys (not the fetched channel-meta
  // list yet) — re-validated and applied once channelMeta loads, then forgotten.
  const restoreChannelSelRef = useRef(
    initialParams?.selected?.length ? { selected: initialParams.selected, focusKey: initialParams.focusKey } : null);

  const [overviewRaw, setOverview] = useState([]); // channel blocks (no hist), logged units
  const [detailRaw, setDetail] = useState(null);   // single focused channel block (with hist)
  const [hiddenSessions, setHiddenSessions] = useState(new Set());  // client-side filter
  const [threshold, setThreshold] = useState(initialParams?.threshold ?? null);
  const [thrAbove, setThrAbove] = useState(initialParams?.thrAbove ?? true);
  const [detailSort, setDetailSort] = useState(initialParams?.detailSort || { key: 'date', dir: 'asc' });
  const [ovSort, setOvSort] = useState(initialParams?.ovSort || { key: 'label', dir: 'asc' });
  // The default-threshold effect below normally resets `threshold` whenever the
  // focused channel changes — including the very first time it's set from a
  // restore. Skip that one time so a restored threshold survives the load.
  const skipFirstThresholdDefaultRef = useRef(initialParams?.threshold != null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const trendChartRef = useRef(null);
  const [trendChartWidth, setTrendChartWidth] = useState(0);
  const [trendTooltipSide, setTrendTooltipSide] = useState('right');

  // ── Admin: view another driver's channel stats (mirrors ProgressPage) ─────
  const isAdmin = !!currentUser?.is_admin;
  const [adminMode, setAdminMode] = useState(!!(isAdmin && initialParams?.adminMode));
  const [viewUser, setViewUser] = useState(isAdmin ? (initialParams?.viewUser || null) : null);   // {id, name} | null (= self)
  const [driverQuery, setDriverQuery] = useState('');
  const [driverResults, setDriverResults] = useState([]);
  const viewUserId = viewUser?.id || null;

  // Debounced driver search (reuses the sharing user-search endpoint).
  useEffect(() => {
    if (!adminMode || driverQuery.trim().length < 2) { setDriverResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      searchUsers(driverQuery.trim())
        .then(rs => !cancelled && setDriverResults(rs))
        .catch(() => !cancelled && setDriverResults([]));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [adminMode, driverQuery]);

  const pickDriver = useCallback((u) => {
    setViewUser({ id: u.id, name: u.display_name || u.email });
    setDriverQuery(''); setDriverResults([]);
    setTrackId(null); setVehicleId(null);   // reload the impersonated driver's data
  }, []);

  const clearDriver = useCallback(() => {
    setViewUser(null);
    setTrackId(null); setVehicleId(null);
  }, []);

  // ── Track list (career rollup gives every track the driver has driven) ────
  useEffect(() => {
    getCareerOverview(viewUserId).then(rows => {
      setTracks(rows || []);
      setTrackId(prev => prev || rows?.[0]?.track_id || null);
    }).catch(e => setError(String(e.message || e)));
  }, [viewUserId]);

  // ── Vehicles at the selected track ────────────────────────────────────────
  useEffect(() => {
    if (!trackId) { setVehicles([]); setVehicleId(null); return; }
    let cancelled = false;
    getUserTrackVehicles(trackId, viewUserId).then(vs => {
      if (cancelled) return;
      setVehicles(vs || []);
      setVehicleId(prev => (vs?.some(v => v.id === prev) ? prev : (vs?.[0]?.id || null)));
    }).catch(() => !cancelled && setVehicles([]));
    return () => { cancelled = true; };
  }, [trackId, viewUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Channels available for the scope ──────────────────────────────────────
  useEffect(() => {
    if (!trackId || !vehicleId) { setChannelMeta([]); setSelected(new Set()); return; }
    let cancelled = false;
    getChannelStatChannels(trackId, vehicleId, viewUserId).then(res => {
      if (cancelled) return;
      const chs = res.channels || [];
      setChannelMeta(chs);
      setTotalSessions(res.total_sessions || 0);
      const restore = restoreChannelSelRef.current;
      restoreChannelSelRef.current = null; // one-shot regardless of outcome
      const validRestoreSel = restore
        ? new Set(restore.selected.filter(k => chs.some(c => c.key === k))) : null;
      if (validRestoreSel?.size) {
        setSelected(validRestoreSel);
        setFocusKey(chs.some(c => c.key === restore.focusKey) ? restore.focusKey : [...validRestoreSel][0]);
        return;
      }
      // Default selection: curated channels present in the scope, minus
      // batt_volt — on an AiM Solo that's often the logger's own battery, not
      // the car's, so it's noise by default (still selectable in the picker).
      const def = chs.filter(c => c.curated && c.key !== 'batt_volt').map(c => c.key);
      const defSet = new Set(def.length ? def : chs.slice(0, 4).map(c => c.key));
      setSelected(defSet);
      setFocusKey(prev => (defSet.has(prev) ? prev : (def[0] || chs[0]?.key || null)));
    }).catch(e => !cancelled && setError(String(e.message || e)));
    return () => { cancelled = true; };
  }, [trackId, vehicleId, viewUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Overview stats for the selected channels (no histograms) ──────────────
  useEffect(() => {
    if (!trackId || !vehicleId || selected.size === 0) { setOverview([]); return; }
    let cancelled = false;
    setLoading(true);
    getChannelStats(trackId, vehicleId, {
      channels: [...selected], lapBasis, userId: viewUserId,
    }).then(res => {
      if (cancelled) return;
      setOverview(res.channels || []);
      setLoading(false);
    }).catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [trackId, vehicleId, lapBasis, selected, viewUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detail (focused channel) with histograms for the threshold band ───────
  useEffect(() => {
    if (!trackId || !vehicleId || !focusKey) { setDetail(null); return; }
    let cancelled = false;
    getChannelStats(trackId, vehicleId, {
      channels: [focusKey], lapBasis, userId: viewUserId,
    }).then(res => {
      if (cancelled) return;
      setDetail(res.channels?.[0] || null);
    }).catch(() => !cancelled && setDetail(null));
    return () => { cancelled = true; };
  }, [trackId, vehicleId, lapBasis, focusKey, viewUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply the user's unit preferences (display only — refetch not needed).
  const overview = useMemo(
    () => overviewRaw.map(c => convChannel(c, unitPrefs)), [overviewRaw, unitPrefs]);
  const detail = useMemo(
    () => convChannel(detailRaw, unitPrefs), [detailRaw, unitPrefs]);

  // Threshold domain follows the focused channel's data range.
  const thrDomain = useMemo(() => {
    const ss = detail?.sessions || [];
    if (!ss.length) return null;
    const lo = Math.min(...ss.map(s => s.min));
    const hi = Math.max(...ss.map(s => s.max));
    return hi > lo ? [lo, hi] : [lo - 1, lo + 1];
  }, [detail]);

  // Reset to a sensible default when the focused channel or its display unit
  // changes (so a stale value isn't reinterpreted in the new units)… except the
  // very first time, if a restored threshold is waiting to be preserved instead.
  useEffect(() => {
    if (!thrDomain) return;
    if (skipFirstThresholdDefaultRef.current) { skipFirstThresholdDefaultRef.current = false; return; }
    setThreshold(+(thrDomain[0] + (thrDomain[1] - thrDomain[0]) * 0.75).toFixed(2));
  }, [focusKey, detail?.unit]); // eslint-disable-line react-hooks/exhaustive-deps
  // …and clamp back into range if the domain shifts (e.g. lap-basis change).
  useEffect(() => {
    if (thrDomain && (threshold == null || threshold < thrDomain[0] || threshold > thrDomain[1])) {
      setThreshold(+(thrDomain[0] + (thrDomain[1] - thrDomain[0]) * 0.75).toFixed(2));
    }
  }, [thrDomain]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror the full filter/selection state up to App.jsx (→ URL) so a
  // Back-navigation or deep-link can restore this page exactly as left.
  useEffect(() => {
    onParamsChange?.({
      trackId, vehicleId, lapBasis,
      selected: [...selected], focusKey, showAll, chanFilter,
      threshold, thrAbove, detailSort, ovSort,
      adminMode, viewUser,
    });
  }, [trackId, vehicleId, lapBasis, selected, focusKey, showAll, chanFilter,
      threshold, thrAbove, detailSort, ovSort, adminMode, viewUser, onParamsChange]);

  const toggleChannel = useCallback((key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleSession = useCallback((sid) => {
    setHiddenSessions(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  }, []);

  const grouped = useMemo(() => {
    const by = {};
    for (const c of channelMeta) (by[c.category] ||= []).push(c);
    return CATEGORY_ORDER.filter(cat => by[cat]).map(cat => [cat, by[cat]]);
  }, [channelMeta]);

  // Date-filter bounds from the focused channel's sessions. Bounds shift when
  // the focused channel changes; useDateRangePresets reconciles the range to
  // the new span (snapping to full if it wasn't deliberately narrowed, else
  // clamping in place) instead of silently going stale.
  // Bounds are day-aligned (as on the Upload page) because the slider steps by
  // DAY_MS from its own min — a raw timestamp min leaves max off that grid, so
  // the right handle could never quite reach it.
  const dateBounds = useMemo(() => {
    const ms = (detail?.sessions || [])
      .map(s => Date.parse(s.event_date)).filter(n => !Number.isNaN(n));
    if (!ms.length) return null;
    return {
      min: Math.floor(Math.min(...ms) / DAY_MS) * DAY_MS,
      max: Math.ceil(Math.max(...ms) / DAY_MS) * DAY_MS,
    };
  }, [detail]);

  const {
    range: dateRange, setRange: setDateRange, applyPreset: applyDatePreset,
    sliderMin: dateSliderMin, sliderMax: dateSliderMax, activePreset: activeDatePreset,
  } = useDateRangePresets(dateBounds);

  const passesDate = useCallback((s) => {
    if (!dateRange) return true;
    const t = Date.parse(s.event_date);
    return Number.isNaN(t) ? true : (t >= dateRange[0] && t <= dateRange[1]);
  }, [dateRange]);

  // All sessions seen across the focused channel (for the filter list + chart).
  const detailSessions = useMemo(
    () => (detail?.sessions || []).filter(s => !hiddenSessions.has(s.session_id) && passesDate(s)),
    [detail, hiddenSessions, passesDate]);

  const focusUnit = detail?.unit || '';

  const chartData = useMemo(() => detailSessions.map((s, i) => {
    const band = threshold != null ? inBand(s, threshold, thrAbove) : { t: null, d: null };
    return {
      i, session_id: s.session_id, date: fmtDate(s.event_date),
      min: s.min, max: s.max, mean: s.mean,
      band_t: band.t,
    };
  }), [detailSessions, threshold, thrAbove]);

  useEffect(() => {
    const el = trendChartRef.current;
    if (!el) return undefined;
    const updateWidth = () => setTrendChartWidth(Math.round(el.clientWidth));
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [chartData.length]);

  const focusMeta = channelMeta.find(c => c.key === focusKey);

  // Per-session detail rows (with the threshold band), sorted for the table.
  const detailRows = useMemo(() => {
    const rows = (detail?.sessions || []).filter(passesDate).map(s => {
      const band = threshold != null ? inBand(s, threshold, thrAbove) : { t: null, d: null };
      return { ...s, _t: band.t, _d: band.d };
    });
    return sortRows(rows, detailSort, {
      date: s => s.event_date, laps: s => s.lap_count,
      min: s => s.min, max: s => s.max, mean: s => s.mean,
      std: s => s.std, p95: s => s.p95, time: s => s._t, dist: s => s._d,
    });
  }, [detail, threshold, thrAbove, detailSort, passesDate]);

  // Column totals for the band Time/Dist over the included (non-hidden) sessions.
  // null when nothing contributes, so the cell renders "—" rather than "0".
  const detailTotals = useMemo(() => {
    let t = 0, d = 0, anyT = false, anyD = false;
    detailRows.forEach(s => {
      if (hiddenSessions.has(s.session_id)) return;
      if (s._t != null) { t += s._t; anyT = true; }
      if (s._d != null) { d += s._d; anyD = true; }
    });
    return { t: anyT ? t : null, d: anyD ? d : null };
  }, [detailRows, hiddenSessions]);

  // Overview rows (one per selected channel, aggregated across sessions), sorted.
  const overviewRows = useMemo(() => {
    const rows = overview.map(ch => {
      const ss = ch.sessions || [];
      return {
        ...ch,
        _min: ss.length ? Math.min(...ss.map(s => s.min)) : null,
        _max: ss.length ? Math.max(...ss.map(s => s.max)) : null,
        _avg: ss.length ? ss.reduce((a, s) => a + s.mean, 0) / ss.length : null,
        _n: ss.length,
      };
    });
    return sortRows(rows, ovSort, {
      label: c => c.label, sessions: c => c._n,
      min: c => c._min, max: c => c._max, avg: c => c._avg,
    });
  }, [overview, ovSort]);

  const sortArrow = (sort, key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');
  const toggleSort = (setSort, key) => setSort(prev =>
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}><Activity size={18} /> Channel Statistics</h1>
        <div className={styles.spacer} />
        {viewUser && (
          <span className={styles.viewingChip}>
            Viewing: <strong>{viewUser.name}</strong>
            <button className={styles.chipX} onClick={clearDriver} title="Back to your own channel stats">✕</button>
          </span>
        )}
        {isAdmin && (
          <div className={styles.adminBox}>
            <button
              className={adminMode ? styles.adminOn : styles.adminOff}
              onClick={() => { setAdminMode(a => !a); setDriverQuery(''); setDriverResults([]); }}
              title="View another driver's channel stats (admin)">
              ⚙ Admin{adminMode ? '' : ' (off)'}
            </button>
            {adminMode && (
              <div className={styles.driverSearch}>
                <input type="text" value={driverQuery} placeholder="Search driver…"
                  onChange={e => setDriverQuery(e.target.value)} />
                {driverResults.length > 0 && (
                  <div className={styles.driverResults}>
                    {driverResults.map(u => (
                      <div key={u.id} className={styles.driverResult} onClick={() => pickDriver(u)}>
                        {u.display_name || u.email}
                        {u.display_name && <span className={styles.muted}> · {u.email}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Scope controls ─────────────────────────────────────────────── */}
      <div className={styles.controls}>
        <label className={styles.ctl}>
          <span>Track</span>
          <select value={trackId || ''} onChange={e => setTrackId(e.target.value || null)}>
            {tracks.map(t => <option key={t.track_id} value={t.track_id}>{t.track_name}</option>)}
          </select>
        </label>
        <label className={styles.ctl}>
          <span>Vehicle</span>
          <select value={vehicleId || ''} onChange={e => setVehicleId(e.target.value || null)}>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
        <div className={styles.ctl}>
          <span>Lap basis</span>
          <div className={styles.segmented}>
            {LAP_BASES.map(b => (
              <button key={b.key}
                className={lapBasis === b.key ? styles.segOn : styles.seg}
                onClick={() => setLapBasis(b.key)}>{b.label}</button>
            ))}
          </div>
        </div>
        <div className={styles.scopeNote}>
          {totalSessions ? `${totalSessions} session${totalSessions === 1 ? '' : 's'} in scope` : ''}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.body}>
        {/* ── Channel picker ──────────────────────────────────────────── */}
        <aside className={styles.picker}>
          <div className={styles.pickerHead}>Channels</div>
          <div className={styles.pickerLegend}>
            <span><CheckSquare size={12} /> Add to Overview table</span>
            <span><LineChartIcon size={12} /> Click a name to chart it</span>
          </div>
          <input className={styles.filter} type="search" placeholder="Filter channels…"
            value={chanFilter} onChange={e => setChanFilter(e.target.value)} />
          {grouped.map(([cat, chs]) => {
            const q = chanFilter.trim().toLowerCase();
            // A filter searches every channel (curated + generic); otherwise the
            // list collapses to curated unless "show all" is on.
            const visible = q
              ? chs.filter(c => c.label.toLowerCase().includes(q))
              : (showAll ? chs : chs.filter(c => c.curated));
            if (!visible.length) return null;
            return (
              <div key={cat} className={styles.catGroup}>
                <div className={styles.catName}>{cat}</div>
                {visible.map(c => (
                  <div key={c.key}
                    className={`${styles.chRow} ${focusKey === c.key ? styles.chFocus : ''}`}>
                    <input type="checkbox" checked={selected.has(c.key)}
                      onChange={() => toggleChannel(c.key)}
                      title="Include in the Overview table below" />
                    <button className={styles.chLabel} onClick={() => setFocusKey(c.key)}
                      title="Chart this channel (detail + threshold band)">
                      <LineChartIcon size={13} className={styles.chIcon} />
                      {c.label}
                    </button>
                    <span className={styles.coverage}
                      title={`${c.n_sessions} of ${totalSessions} sessions`}>
                      {Math.round((c.coverage || 0) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
          {chanFilter.trim() && !channelMeta.some(c => c.label.toLowerCase().includes(chanFilter.trim().toLowerCase())) && (
            <div className={styles.noMatch}>No channels match “{chanFilter.trim()}”.</div>
          )}
          {!chanFilter.trim() && channelMeta.some(c => !c.curated) && (
            <button className={styles.showAll} onClick={() => setShowAll(v => !v)}>
              {showAll ? 'Show curated only' : 'Show all available channels'}
            </button>
          )}
        </aside>

        {/* ── Main: focused-channel detail + overview table ───────────── */}
        <main className={styles.main}>
          {/* Date filter (defaults to all sessions) */}
          {dateBounds && dateBounds.max > dateBounds.min && (
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <span>Date range</span>
                <span className={styles.dateSpan}>
                  {fmtDate(new Date(dateRange ? dateRange[0] : dateBounds.min))}
                  {' – '}
                  {fmtDate(new Date(dateRange ? dateRange[1] : dateBounds.max))}
                  {DATE_PRESETS.map(([key, label]) => (
                    <button key={key}
                            className={`${styles.clearBtn} ${activeDatePreset === key ? styles.presetActive : ''}`}
                            onClick={() => applyDatePreset(key)}>
                      {label}
                    </button>
                  ))}
                </span>
              </div>
              <RangeSlider min={dateSliderMin} max={dateSliderMax} step={DAY_MS}
                value={dateRange} onChange={setDateRange} width="100%" format={ms => fmtDate(new Date(ms))} />
            </section>
          )}

          {/* Trend chart for the focused channel */}
          <section className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.chartingHdr}>
                <LineChartIcon size={14} /> Charting: {focusMeta?.label || 'Channel'}
                {focusUnit ? ` (${focusUnit})` : ''} — per session
              </span>
              <div className={styles.legend}>
                <span><i style={{ background: '#e8411a' }} />Max</span>
                <span><i style={{ background: '#fbbf24' }} />Mean</span>
                <span><i style={{ background: '#60a5fa' }} />Min</span>
              </div>
            </div>
            {chartData.length ? (
              <div className={styles.trendChart} ref={trendChartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}
                    accessibilityLayer={false}
                    onMouseMove={e => {
                      if (!unitPrefs?.anchoredTooltip || !trendChartWidth || !e?.activeCoordinate) return;
                      const frac = e.activeCoordinate.x / trendChartWidth;
                      if (frac < 0.45) setTrendTooltipSide('right');
                      else if (frac > 0.55) setTrendTooltipSide('left');
                    }}
                    onClick={e => {
                      const row = chartData[e?.activeTooltipIndex];
                      if (row?.session_id) onLoadSession?.(row.session_id);
                    }}>
                    <CartesianGrid stroke="#2c2c2c" strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fill: '#a8a8a8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#a8a8a8', fontSize: 11 }} width={48} />
                    <Tooltip
                      contentStyle={{ background: '#111', border: '1px solid #2c2c2c' }}
                      position={unitPrefs?.anchoredTooltip ? {
                        x: trendTooltipSide === 'left'
                          ? 10
                          : Math.max(10, trendChartWidth - 180),
                        y: 10,
                      } : undefined}
                      wrapperStyle={unitPrefs?.anchoredTooltip
                        ? { width: 170, pointerEvents: 'none' }
                        : undefined}
                    />
                    <Line type="monotone" dataKey="max" stroke="#e8411a" strokeWidth={1.8} dot={false} name="max" />
                    <Line type="monotone" dataKey="mean" stroke="#fbbf24" strokeWidth={2} dot name="mean" />
                    <Line type="monotone" dataKey="min" stroke="#60a5fa" strokeWidth={1.8} dot={false} name="min" />
                    {threshold != null && <ReferenceLine y={threshold} stroke="#34d399" strokeDasharray="5 4" />}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className={styles.empty}>{loading ? 'Loading…' : 'No data for this channel/scope.'}</div>
            )}
          </section>

          {/* Threshold band */}
          {thrDomain && (
            <section className={styles.card}>
              <div className={styles.cardHead}>
                <span>Threshold band — {focusMeta?.label || 'channel'}</span>
                <div className={styles.thrCtl}>
                  <button className={thrAbove ? styles.segOn : styles.seg}
                    onClick={() => setThrAbove(true)}>Above</button>
                  <button className={!thrAbove ? styles.segOn : styles.seg}
                    onClick={() => setThrAbove(false)}>Below</button>
                  <input type="range" min={thrDomain[0]} max={thrDomain[1]}
                    step={(thrDomain[1] - thrDomain[0]) / 256}
                    value={threshold ?? thrDomain[0]}
                    onChange={e => setThreshold(parseFloat(e.target.value))} />
                  <input type="number" className={styles.thrInput}
                    min={thrDomain[0]} max={thrDomain[1]} step="any"
                    value={threshold ?? ''}
                    onChange={e => {
                      const v = parseFloat(e.target.value);
                      setThreshold(Number.isNaN(v) ? null : v);
                    }}
                    onBlur={e => {
                      const v = parseFloat(e.target.value);
                      if (Number.isNaN(v)) return;
                      const c = Math.min(thrDomain[1], Math.max(thrDomain[0], v));
                      setThreshold(+c.toFixed(2));
                    }} />
                  {focusUnit && <span className={styles.muted}>{focusUnit}</span>}
                </div>
              </div>
              <div className={styles.hint}>
                Time &amp; distance with {focusMeta?.label || 'the channel'} {thrAbove ? 'at or above' : 'at or below'} the threshold, per session.
              </div>
            </section>
          )}

          {/* Per-session detail table for the focused channel */}
          <section className={styles.card}>
            <div className={styles.cardHead}><span>Per-session — {focusMeta?.label || ''} (charting)</span></div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th title="Uncheck to hide a session from the chart above">✓</th>
                    <th title={TIP.session} onClick={() => toggleSort(setDetailSort, 'date')}>Session{sortArrow(detailSort, 'date')}</th>
                    <th title={TIP.laps} onClick={() => toggleSort(setDetailSort, 'laps')}>Laps{sortArrow(detailSort, 'laps')}</th>
                    <th title={TIP.min} onClick={() => toggleSort(setDetailSort, 'min')}>Min{sortArrow(detailSort, 'min')}</th>
                    <th title={TIP.max} onClick={() => toggleSort(setDetailSort, 'max')}>Max{sortArrow(detailSort, 'max')}</th>
                    <th title={TIP.mean} onClick={() => toggleSort(setDetailSort, 'mean')}>Mean{sortArrow(detailSort, 'mean')}</th>
                    <th title={TIP.std} onClick={() => toggleSort(setDetailSort, 'std')}>σ{sortArrow(detailSort, 'std')}</th>
                    <th title={TIP.p95} onClick={() => toggleSort(setDetailSort, 'p95')}>P95{sortArrow(detailSort, 'p95')}</th>
                    <th title={thrAbove ? TIP.timeAbove : TIP.timeBelow} onClick={() => toggleSort(setDetailSort, 'time')}>{thrAbove ? 'Time ≥' : 'Time ≤'}{sortArrow(detailSort, 'time')}</th>
                    <th title={thrAbove ? TIP.distAbove : TIP.distBelow} onClick={() => toggleSort(setDetailSort, 'dist')}>Dist{sortArrow(detailSort, 'dist')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map(s => {
                    const hidden = hiddenSessions.has(s.session_id);
                    return (
                      <tr key={s.session_id} className={hidden ? styles.rowHidden : ''}>
                        <td><input type="checkbox" checked={!hidden}
                          onChange={() => toggleSession(s.session_id)} /></td>
                        <td className={styles.linkCell} onClick={() => onLoadSession?.(s.session_id)}>
                          {fmtDate(s.event_date)}</td>
                        <td>{s.lap_count}</td>
                        <td>{fmtNum(s.min, focusUnit)}</td>
                        <td>{fmtNum(s.max, focusUnit)}</td>
                        <td>{fmtNum(s.mean, focusUnit)}</td>
                        <td>{fmtNum(s.std, focusUnit)}</td>
                        <td>{fmtNum(s.p95, focusUnit)}</td>
                        <td>{fmtSecs(s._t)}</td>
                        <td>{fmtDist(s._d, unitPrefs)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td></td>
                    <td>Total</td>
                    <td colSpan={6}></td>
                    <td>{fmtSecs(detailTotals.t)}</td>
                    <td>{fmtDist(detailTotals.d, unitPrefs)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Overview: one row per selected channel (aggregate across sessions) */}
          <section className={styles.card}>
            <div className={styles.cardHead}><span>Overview — selected channels</span></div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th title={TIP.ovChannel} onClick={() => toggleSort(setOvSort, 'label')}>Channel{sortArrow(ovSort, 'label')}</th>
                    <th title={TIP.ovSessions} onClick={() => toggleSort(setOvSort, 'sessions')}>Sessions{sortArrow(ovSort, 'sessions')}</th>
                    <th title={TIP.ovMin} onClick={() => toggleSort(setOvSort, 'min')}>Min{sortArrow(ovSort, 'min')}</th>
                    <th title={TIP.ovMax} onClick={() => toggleSort(setOvSort, 'max')}>Max{sortArrow(ovSort, 'max')}</th>
                    <th title={TIP.ovAvg} onClick={() => toggleSort(setOvSort, 'avg')}>Avg of means{sortArrow(ovSort, 'avg')}</th>
                    <th title={TIP.ovUnit}>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {overviewRows.map(ch => (
                    <tr key={ch.key}
                      className={focusKey === ch.key ? styles.rowFocus : styles.linkRow}
                      onClick={() => setFocusKey(ch.key)}>
                      <td>{ch.label}</td>
                      <td>{ch._n}</td>
                      <td>{fmtNum(ch._min, '')}</td>
                      <td>{fmtNum(ch._max, '')}</td>
                      <td>{fmtNum(ch._avg, '')}</td>
                      <td className={styles.muted}>{ch.unit || '—'}</td>
                    </tr>
                  ))}
                  {!overviewRows.length && (
                    <tr><td colSpan={6} className={styles.empty}>
                      {loading ? 'Loading…' : 'Select channels to compare.'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
